import React, {
  useEffect, useLayoutEffect, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  makeStyles,
  SvgIcon,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { Menu as MenuIcon } from 'react-feather';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import ThemeModeToggle from 'src/components/ThemeModeToggle';

/** Matches the single-row `.toolbar` height — keep in sync with page offsets below the fixed AppBar. */
export const HOME_TOP_BAR_HEIGHT_MOBILE_PX = 48;
export const HOME_TOP_BAR_HEIGHT_PX = 64;
export const HOME_TOP_BAR_BG = '#1f242d';

/**
 * TopBar writes its rendered height here. Guest desktops can grow to two rows
 * (guide links, then logo + login) before the menu icon.
 */
export const HOME_TOP_BAR_HEIGHT_VAR = '--home-top-bar-height';

export function homeTopBarHeightCss(fallbackPx) {
  return `var(${HOME_TOP_BAR_HEIGHT_VAR}, ${fallbackPx}px)`;
}

/** Minimum clearance between the nav links and the logo / login block before rearranging. */
const NAV_COLLAPSE_BUFFER_PX = 16;

const useStyles = makeStyles((theme) => ({
  root: {
    zIndex: theme.zIndex.drawer + 100,
    backgroundColor: HOME_TOP_BAR_BG,
    boxShadow: 'none',
  },
  toolbar: {
    minHeight: HOME_TOP_BAR_HEIGHT_MOBILE_PX,
    height: HOME_TOP_BAR_HEIGHT_MOBILE_PX,
    position: 'relative',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      minHeight: HOME_TOP_BAR_HEIGHT_PX,
      height: HOME_TOP_BAR_HEIGHT_PX,
    },
  },
  navButtons: {
    fontSize: "15px",
    margin: "0px",
    marginLeft: "10px",
    textTransform: "none",
    borderRadius: 0,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    minWidth: 'auto',
    maxWidth: 'none',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
    },
    '& .MuiButton-label': {
      whiteSpace: 'nowrap',
      color: '#ffffff',
    },
  },
  navButtonsActive: {
    fontSize: "15px",
    margin: "0px",
    marginLeft: "10px",
    textTransform: "none",
    color: "rgb(0, 158, 218)",
    borderRadius: "0px",
    lineHeight: 1.2,
    borderBottom: "solid 2px rgb(0, 158, 218)",
    paddingBottom: "0px",
    whiteSpace: 'nowrap',
    flexShrink: 0,
    minWidth: 'auto',
    maxWidth: 'none',
    '& .MuiButton-label': {
      whiteSpace: 'nowrap',
      color: 'rgb(0, 158, 218)',
    },
  },
  navButtonsActiveMobile: {
    fontSize: "17px",
    margin: "0px",
    textTransform: "none",
    color: "white"
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 5,
    flexWrap: 'nowrap',
    width: 'max-content',
    maxWidth: 'none',
  },
  navLinksDefault: {
    flex: 1,
    justifyContent: 'center',
    minWidth: 0,
  },
  navLinksScreenCenter: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
  },
  loginButton: {
    position: 'relative',
    backgroundColor: '#009eda',
    marginLeft: 8,
    marginRight: 16,
    borderRadius: 25,
    height: 40,
    width: 83,
    textTransform: 'none',
    color: 'white',
    fontSize: 14,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: '#009eda',
    },
  },
  imageDesktop: {
    width: '65px',
    marginLeft: "16px",
    height: "35px",
    cursor: 'pointer',
  },
  imageHorizontal: {
    width: 'auto',
    height: 30,
    maxHeight: 30,
    marginLeft: 16,
    objectFit: 'contain',
    cursor: 'pointer',
  },
  rightBlock: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    flexShrink: 0,
  },
  /**
   * Guest desktop, mid width: guide links keep the full first row.
   * Logo and login move to a second row instead of the menu icon.
   */
  toolbarStacked: {
    '&&': {
      height: 'auto !important',
      maxHeight: 'none !important',
      minHeight: HOME_TOP_BAR_HEIGHT_PX,
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  stackedNavRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: 36,
  },
  stackedNavLinks: {
    '&&': {
      marginTop: 0,
    },
    flex: '0 0 auto',
    justifyContent: 'center',
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 40,
    marginTop: 6,
  },
  /** Invisible copy of the desktop row, used only to measure natural widths. */
  measurer: {
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    width: 'max-content',
    visibility: 'hidden',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
  measurerPart: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
}));

function TopBar({
  className,
  onMobileNavOpen,
  hideLoginButton,
  centerNavLinks = false,
  logoSrc = '/static/news/1000containers-horizontal-white.svg',
  ...rest
}) {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  const [pathname, setPathName] = useState(null);
  const [desktopLayout, setDesktopLayout] = useState('single');
  const toolbarRef = useRef(null);
  const measureLogoRef = useRef(null);
  const measureNavRef = useRef(null);
  const measureRightRef = useRef(null);

  const isHorizontalLogo = logoSrc.includes('horizontal');
  const logoClassName = isHorizontalLogo ? classes.imageHorizontal : classes.imageDesktop;
  const showThemeToggle = pathname === '/app/solar-panels';
  const showMenu = isSmDown || desktopLayout === 'menu';
  const showStacked = !showMenu && desktopLayout === 'stacked';

  const goToInventory = () => {
    history.push('/app/solar-panels');
  };

  useEffect(() => {
    if (location.pathname) {
      setPathName(location.pathname);
    }
  }, [location]);

  useLayoutEffect(() => {
    if (isSmDown) return undefined;
    const toolbar = toolbarRef.current;
    const logoEl = measureLogoRef.current;
    const navEl = measureNavRef.current;
    const rightEl = measureRightRef.current;
    if (!toolbar || !logoEl || !navEl || !rightEl) return undefined;

    const measure = () => {
      const style = window.getComputedStyle(toolbar);
      const available = toolbar.clientWidth
        - parseFloat(style.paddingLeft || 0)
        - parseFloat(style.paddingRight || 0);
      const logoW = logoEl.getBoundingClientRect().width;
      const navW = navEl.getBoundingClientRect().width;
      const rightW = rightEl.getBoundingClientRect().width;

      // Extra room once collapsed so a scrollbar from the taller bar cannot bounce the layout.
      const HOLD_PX = 32;
      setDesktopLayout((prev) => {
        const singleBuffer = prev === 'single'
          ? NAV_COLLAPSE_BUFFER_PX
          : NAV_COLLAPSE_BUFFER_PX + HOLD_PX;
        const tightBuffer = prev === 'menu'
          ? NAV_COLLAPSE_BUFFER_PX + HOLD_PX
          : NAV_COLLAPSE_BUFFER_PX;

        const singleRowOverflows = centerNavLinks
          ? navW / 2 + Math.max(logoW, rightW) + singleBuffer > available / 2
          : logoW + navW + rightW + singleBuffer * 2 > available;
        const guidesOverflow = navW + tightBuffer > available;
        const brandRowOverflows = logoW + rightW + tightBuffer > available;

        let next = 'single';
        if (singleRowOverflows) {
          // Guests: drop logo + login under the guide links while those links still fit.
          next = (!hideLoginButton && !guidesOverflow && !brandRowOverflows)
            ? 'stacked'
            : 'menu';
        }
        return prev === next ? prev : next;
      });
    };

    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    [toolbar, logoEl, navEl, rightEl].forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isSmDown, centerNavLinks, hideLoginButton]);

  useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return undefined;

    const publish = () => {
      const height = toolbar.offsetHeight;
      if (height > 0) {
        document.documentElement.style.setProperty(HOME_TOP_BAR_HEIGHT_VAR, `${height}px`);
      }
    };

    publish();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(publish);
    observer.observe(toolbar);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(HOME_TOP_BAR_HEIGHT_VAR);
    };
  }, [showMenu, showStacked]);

  const renderLogo = (ref) => (
    <img
      ref={ref}
      className={logoClassName}
      alt="1000 Containers"
      src={logoSrc}
      onClick={goToInventory}
      role="button"
    />
  );

  const renderNavButtons = () => (
    <>
      <Button size='small' className={classes.navButtons} onClick={() => window.location.href = "/home"}>
        Home
      </Button>
      <Button size='small' className={classes.navButtons} onClick={() => window.location.href = "/about-us"}>
        About Us
      </Button>
      <Button size='small' className={pathname === "/app/solar-panels/news" ? classes.navButtonsActive : classes.navButtons} onClick={() => history.push("/app/solar-panels/news")}>
        News
      </Button>
      <Button size='small' className={pathname === "/app/solar-panels/events" ? classes.navButtonsActive : classes.navButtons} onClick={() => history.push("/app/solar-panels/events")}>
        Events
      </Button>
      <Button size='small' className={(pathname === "/app/solar-panels" || pathname === "/login" || pathname === "/register" || pathname === "/guest/login") ? classes.navButtonsActive : classes.navButtons} onClick={() => history.push("/app/solar-panels")}>
        See Full Inventory
      </Button>
      <Button size='small' className={classes.navButtons} onClick={() => window.location.href = "/re-plus-las-vegas-2026"}>
        Re+ 2026
      </Button>
      <Button size='small' className={classes.navButtons} onClick={() => window.location.href = "/safe-harbor"}>
        Safe Harbor
      </Button>
      <Button size='small' className={classes.navButtons} onClick={() => window.location.href = "/custom-procurement"}>
        Custom Procurement
      </Button>
      <Button size='small' className={ classes.navButtons} onClick={() => window.location.href = "/contact"}>
        Contact Us
      </Button>
    </>
  );

  const renderRightContent = () => (
    <>
      {showThemeToggle ? (
        <ThemeModeToggle variant="topBar" />
      ) : null}
      {!hideLoginButton && (
        <Button size='small' className={classes.loginButton} style={{ marginLeft: 0 }} onClick={() => history.push("/login")}>
          <Box component="span" style={{ display: "inline-block", width: "7px", height: "7px", background: "white", borderRadius: "50%", marginRight: "6px", flexShrink: 0 }}></Box>Login
        </Button>
      )}
    </>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Toolbar
        ref={toolbarRef}
        className={clsx(classes.toolbar, showStacked && classes.toolbarStacked)}
      >
        {!isSmDown && (
          <Box className={classes.measurer} aria-hidden="true">
            <Box ref={measureLogoRef} className={classes.measurerPart}>
              {renderLogo()}
            </Box>
            <Box ref={measureNavRef} className={clsx(classes.navLinks, classes.measurerPart)}>
              {renderNavButtons()}
            </Box>
            <Box ref={measureRightRef} className={classes.measurerPart}>
              {renderRightContent()}
            </Box>
          </Box>
        )}

        {showMenu ? (
          <>
            {renderLogo()}
            {showThemeToggle ? (
              <Box
                style={{
                  position: 'absolute',
                  right: 64,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ThemeModeToggle variant="topBar" />
              </Box>
            ) : null}
            <IconButton
              onClick={onMobileNavOpen}
              aria-label="Open menu"
              style={{
                position: 'absolute',
                right: 24,
              }}
            >
              <SvgIcon style={{fontSize:"24px"}}>
                <MenuIcon color='white' />
              </SvgIcon>
            </IconButton>
          </>
        ) : showStacked ? (
          <>
            <Box className={classes.stackedNavRow}>
              <Box className={clsx(classes.navLinks, classes.stackedNavLinks)}>
                {renderNavButtons()}
              </Box>
            </Box>
            <Box className={classes.brandRow}>
              {renderLogo()}
              <Box className={classes.rightBlock}>
                {renderRightContent()}
              </Box>
            </Box>
          </>
        ) : (
          <>
            {renderLogo()}
            <Box
              className={clsx(
                classes.navLinks,
                centerNavLinks ? classes.navLinksScreenCenter : classes.navLinksDefault
              )}
            >
              {renderNavButtons()}
            </Box>
            <Box className={classes.rightBlock}>
              {renderRightContent()}
            </Box>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

TopBar.propTypes = {
  className: PropTypes.string,
  onMobileNavOpen: PropTypes.func,
  hideLoginButton: PropTypes.bool,
  centerNavLinks: PropTypes.bool,
  logoSrc: PropTypes.string,
};

export default TopBar;
